// ./reservebar-button-state.js

import Liquid from '@liquidcloud/sdk';
import {
	buildAddressParams,
	createProductStoreKey,
	getLiquidAddressObject,
	getReservebarStore,
	selectPreferredRetailer
} from './reservebar-config';
import {getAddresParams} from 'Assets/js/modules/library-modules/reservebar/reservebar-cart-modal';

function setButtonLabel(btn, text) {
	const span = btn.querySelector('.post-type-filter-grid-v2__cta-span');
	if (span) span.textContent = text;
	else btn.textContent = text;
}

// If you already have these helpers in a separate file (as I suggested earlier),
// you can import them from there instead of duplicating them.
let liquidClientPromise = null;
function getLiquidClient() {
	if (!liquidClientPromise) {
		liquidClientPromise = Liquid({
			clientId: 'cc615190c9d297b935482113af23d8b1',
			env: 'production'
		});
	}
	return liquidClientPromise;
}

function getReservebarLabel(key, fallback) {
	if (
		typeof window !== 'undefined' &&
		window.reservebarPopupConfig &&
		Object.prototype.hasOwnProperty.call(window.reservebarPopupConfig, key)
	) {
		const val = window.reservebarPopupConfig[key];
		if (typeof val === 'string' && val.trim() !== '') {
			return val;
		}
	}
	return fallback;
}

// Which buttons do we control (you can change the selector to suit your HTML)
const BUTTON_SELECTOR = '.reservebar-button';

/**
 * Setup of all ReserveBar buttons
 * */
export function initReservebarButtons() {
	refreshReservebarButtons();

	window.addEventListener('reservebar:address-updated', () => {
		refreshReservebarButtons();
	});

	// Vue grid buttons mount after DOMContentLoaded
	window.addEventListener('reservebar:buttons-added', () => {
		refreshReservebarButtons();
	});
}

export function refreshReservebarButtons() {
	const buttons = Array.from(document.querySelectorAll(BUTTON_SELECTOR));

	if (!buttons.length) {
		return;
	}

	const addrObj = getLiquidAddressObject();

	// If there is no address, all buttons have the same simple state.
	if (!addrObj) {
		buttons.forEach((btn) => {
			setupButtonNeedAddress(btn);
		});
		return;
	}

	const store = getReservebarStore();

	getLiquidClient().then((liquid) => {
		buttons.forEach((btn) => {
			const gid = btn.getAttribute('reservebar-liquid-id');
			if (!gid || gid == 'false') {
				setupButtonNoId(btn);
				return;
			}

			const storeKey = createProductStoreKey(gid, addrObj);

			// Try to use cached state from store first
			if (store && storeKey) {
				const cached = store.getProduct(storeKey);
				if (cached) {
					applyProductStateToButton(btn, cached);
					return;
				}
			}

			// If nothing in cache – go to loading state and call API
			setupButtonLoading(btn);

			const params = getAddresParams(gid);
			loadButtonProductState(liquid, btn, params, storeKey);
		});
	});
}

function applyProductStateToButton(btn, productState) {
	if (!productState || !productState.hasPrice) {
		setupButtonUnavailable(btn);
		return;
	}

	setupButtonAvailable(btn, productState.price);
}

function applyButtonMode(btn, mode) {
	btn.classList.remove(
		'reservebar-set-address-trigger',
		'reservebar-cart-trigger',
		'reservebar-product-link'
	);

	if (mode === 'need-address') {
		btn.classList.add('reservebar-set-address-trigger');
	} else if (mode === 'cart') {
		btn.classList.add('reservebar-cart-trigger');
	} else if (mode === 'product-link') {
		btn.classList.add('reservebar-product-link');
	}
}

/**
 * Button states
 * */
function setupButtonNeedAddress(btn) {
	btn.dataset.reservebarState = 'need-address';
	applyButtonMode(btn, 'need-address');
	btn.disabled = false;
	btn.classList.remove('is-loading');

	const label =
		btn.getAttribute('data-label-check') ||
		getReservebarLabel(
			'reservebar_check_price_label',
			'Check Price and Availability'
		);

	setButtonLabel(btn, label);
}

function setupButtonNoId(btn) {
	btn.dataset.reservebarState = 'no-id';
	btn.disabled = true;
	btn.classList.remove('is-loading');
	setButtonLabel(
		btn,
		btn.getAttribute('data-label-learnmore') ||
			getReservebarLabel('reservebar_learn_more_label', 'Learn more')
	);
}

function setupButtonLoading(btn) {
	btn.dataset.reservebarState = 'loading';
	btn.disabled = true;
	btn.classList.add('is-loading');
	setButtonLabel(
		btn,
		getReservebarLabel('reservebar_button_loading_label', 'Checking...')
	);
}

function setupButtonUnavailable(btn) {
	btn.dataset.reservebarState = 'product-link';
	applyButtonMode(btn, 'product-link');
	btn.disabled = false;
	btn.classList.remove('is-loading');
	setButtonLabel(
		btn,
		btn.getAttribute('data-learn-more') ||
			getReservebarLabel('reservebar_learn_more_label', 'Learn more')
	);
}
function setupButtonAvailable(btn, price) {
	btn.dataset.reservebarState = 'available';
	applyButtonMode(btn, 'cart');
	btn.disabled = false;
	btn.classList.remove('is-loading');

	if (price) {
		const fallbackLabel =
			btn.getAttribute('data-buy-now') ||
			getReservebarLabel('reservebar_starting_at_label', 'Starting at');

		setButtonLabel(btn, fallbackLabel);
	} else {
		const fallbackLabel =
			btn.getAttribute('data-buy-now') ||
			btn.getAttribute('data-label-check') ||
			getReservebarLabel(
				'reservebar_check_price_label',
				'Check Price and Availability'
			);
		setButtonLabel(btn, fallbackLabel);
	}
}

/**
 * Calling liquid.product for a single button and updating its status
 */
function loadButtonProductState(liquid, btn, params, storeKey) {
	const store = getReservebarStore();

	// 1) Try to retrieve from cache
	if (store && storeKey) {
		const cached = store.getProduct(storeKey);
		if (cached) {
			applyProductStateToButton(btn, cached);
			return;
		}
	}


	// 2) If there is nothing in the cache, go to liquid.product
	liquid
		.product(params)
		.then((products) => {
			let product = null;

			if (Array.isArray(products)) {
				product = products[0] || null;
			} else if (products && Array.isArray(products.products)) {
				product = products.products[0] || null;
			}

			if (!product || !Array.isArray(product.variants)) {
				setupButtonUnavailable(btn);
				return;
			}

			const firstVariant = product.variants[0];
			let price = null;
			let firstRetailer = null;

			if (firstVariant && Array.isArray(firstVariant.retailers)) {
				firstRetailer =
					selectPreferredRetailer(
						firstVariant.retailers,
						firstVariant
					) || null;
				if (firstRetailer && firstRetailer.price) {
					price = firstRetailer.price;
				}
			}

			const productState = {
				hasPrice: !!price,
				price: price || null,
				productId: firstVariant ? firstVariant.productId : null,
				variantId: firstRetailer ? firstRetailer.variantId : null,
				retailer: firstRetailer ? firstRetailer.retailer : null,
				shippingMethod: firstRetailer
					? firstRetailer.shippingMethod
					: null,
				raw: {
					product,
					variant: firstVariant,
					retailer: firstRetailer
				}
			};
			// save to Store
			if (store && storeKey) {
				store.setProduct(storeKey, productState);
			}

			applyProductStateToButton(btn, productState);
		})
		.catch((e) => {
			console.error(
				'[ReserveBar] failed to load product state for button',
				e
			);
			setupButtonUnavailable(btn);
		});
}
